import { JsonObject, JsonProperty } from "typescript-json-serializer";
import {MetadataConfiguration} from "./metadata-configuration";
import {ListItemConfiguration} from "./list-item-configuration";
import {MessageConfiguration} from "./message-configuration";

@JsonObject()
export class LayerListConfiguration {

    /**
     * *Geo.js Property*
     * Groups to add to the layer list
     * --> added to operationalLayers
     */
    @JsonProperty()
    declare groups: ListItemConfiguration[];

    /**
     * *Geo.js Property*
     * Hide items, add actions to items, override settings from webmap (e.g. if item is open)
     * --> goes to operationalLayers
     */
    @JsonProperty()
    declare items: ListItemConfiguration[];

    /**
     * *Geo.js Property*
     * Metadata for layers
     */
    @JsonProperty()
    declare metadata: MetadataConfiguration;

    /**
     * *Geo.js Property*
     * Limit the tree levels. e.g. limitLevel: 1 would list only root level.
     *
     * @default 0 // no limit
     */
    @JsonProperty() limitLevel = 0;
    /**
     * *Geo.js Property*
     * Rebuild the TOC if true.
     *
     * @default false
     */
    @JsonProperty() rebuild = false;
    /**
     * *Geo.js Property*
     * Start the tree at level. e.g. limitLevel: 1 would hide the root level.
     *
     * @default 0 // root Level
     */
    @JsonProperty() startLevel = 0;

    /**
     * *Geo.js Property*
     * If list with layers is hidden.
     * Only shows messages and legend (if configured)
     */
    @JsonProperty()
    declare hideList: boolean;

    /**
     * *Geo.js Property*
     * Metadata for layers
     */
    @JsonProperty()
    declare preContent?: MessageConfiguration[];
    /**
     * *Geo.js Property*
     * Metadata for layers
     */
    @JsonProperty()
    declare postContent?: MessageConfiguration[];

    /**
     * Indicates whether the widget is collapsed.
     *
     * @default false
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-LayerList.html#collapsed)
     */
    @JsonProperty()
    declare collapsed?: boolean;
    /**
     * Indicates whether [list items](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-LayerList-ListItem.html) may be reordered within the list by dragging and dropping.
     *
     * @default false
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-LayerList.html#dragEnabled)
     */
    @JsonProperty()
    declare dragEnabled?: boolean;
    /**
     * Placeholder text used in the filter input if [visibleElements.filter](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-LayerList.html#visibleElements) is true.
     *
     * @default ""
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-LayerList.html#filterPlaceholder)
     */
    @JsonProperty()
    declare filterPlaceholder?: string;
    /**
     * The value of the filter input if [visibleElements.filter](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-LayerList.html#visibleElements) is true.
     *
     * @default ""
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-LayerList.html#filterText)
     */
    @JsonProperty()
    declare filterText?: string;
    /**
     * Indicates the heading level to use for the heading of the widget.
     *
     * @default 2
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-LayerList.html#headingLevel)
     */
    @JsonProperty()
    declare headingLevel?: number;
    /**
     * The widget's default CSS icon class.
     *
     * @deprecated since 4.27. Use {@link module:esri/widgets/LayerList#icon icon} instead.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-LayerList.html#iconClass)
     */
    @JsonProperty()
    declare iconClass?: string;
    /**
     * A function that executes each time a [ListItem](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-LayerList-ListItem.html) is created.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-LayerList.html#listItemCreatedFunction)
     */
    // @JsonProperty()
    // declare listItemCreatedFunction?: ListItemCreatedHandler;
    /**
     * The minimum number of list items required to enable drag and drop reordering with [dragEnabled](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-LayerList.html#dragEnabled).
     *
     * @default 2
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-LayerList.html#minDragEnabledItems)
     */
    @JsonProperty()
    declare minDragEnabledItems?: number;
    /**
     * The minimum number of list items required to display the visibleElements.filter input box.
     *
     * @default 10
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-LayerList.html#minFilterItems)
     */
    @JsonProperty()
    declare minFilterItems?: number;
    /**
     * Indicates whether more than one list item may be selected by the user at a single time.
     *
     * @default false
     * @deprecated since 4.29. Use {@link module:esri/widgets/LayerList#selectionMode selectionMode} instead.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-LayerList.html#multipleSelectionEnabled)
     */
    @JsonProperty()
    declare multipleSelectionEnabled?: boolean;
    /**
     * A collection of selected
     * [ListItem](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-LayerList-ListItem.html)s
     * representing operational layers selected by the user.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-LayerList.html#selectedItems)
     */
    // declare selectedItems?: CollectionProperties<ListItem>;
    /**
     * Indicates whether list items may be selected by the user.
     *
     * @default false
     * @deprecated since 4.29. Use {@link module:esri/widgets/LayerList#selectionMode selectionMode} and {@link module:esri/widgets/LayerList#dragEnabled dragEnabled} instead.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-LayerList.html#selectionEnabled)
     */
    @JsonProperty()
    declare selectionEnabled?: boolean;
    /**
     * Specifies the selection mode.
     *
     * @default "none"
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-LayerList.html#selectionMode)
     */
    @JsonProperty()
    declare selectionMode?: "multiple" | "none" | "single" | "single-persist";
    /**
     * A reference to the [MapView](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html) or [SceneView](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-SceneView.html).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-LayerList.html#view)
     */
    // @JsonProperty()
    // declare view?: (MapViewConfiguration & { type: "2d" }); // | (SceneViewProperties & { type: "3d" });
    /**
     * The view model for this widget.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-LayerList.html#viewModel)
     */
    // @JsonProperty()
    // declare viewModel?: LayerListViewModelProperties;
    /**
     * Determines the icons used to indicate visibility.
     *
     * @default "default"
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-LayerList.html#visibilityAppearance)
     */
    @JsonProperty()
    declare visibilityAppearance?: "default" | "checkbox";
    /**
     * The visible elements that are displayed within the widget.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-LayerList.html#visibleElements)
     */
    // declare visibleElements?: LayerListVisibleElements;

    constructor() {}
}
